import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Box,
  MenuItem,
  Typography,
} from "@mui/material";
import useAdd from "../../api/useAdd";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";
import {
  getFormDirection,
  getRtlSelectProps,
  getRtlTextFieldProps,
} from "../utils/rtlForm";

const ProductAdd = ({ open, onClose, token }) => {
  const { t, i18n } = useTranslation();
  const formDirection = getFormDirection(i18n);
  const { fieldDirection, fieldTextAlign } = formDirection;
  const selectProps = getRtlSelectProps(formDirection);
  const { handleAdd, loading, resetForm } = useAdd("shop/products", token);
  const { data: categoriesData, loading: catLoading } = useFetchObjects(
    ["category"],
    "shop/categories",
    token
  );
  const { data: shopsData, loading: shopLoading } = useFetchObjects(
    ["shop"],
    "shop/shop",
    token
  );

  const categories = categoriesData?.results || [];
  const shops = shopsData?.results || [];
  const hasSingleShop = shops.length === 1;

  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [description, setDescription] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedShop, setSelectedShop] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [brandName, setBrandName] = useState("");

  useEffect(() => {
    if (!shopLoading && hasSingleShop) {
      setSelectedShop(String(shops[0].id));
    }
  }, [shopLoading, hasSingleShop, shops]);

  const handleFileChange = (e) => {
    setImageFile(e.target.files[0]);
  };

  const handleSubmit = () => {
    const formData = new FormData();
    formData.append("name", name.trim());
    formData.append("code", code.trim());
    formData.append("description", description);
    formData.append("category", selectedCategory);
    formData.append("shop", selectedShop);
    formData.append("brand_name", brandName.trim());
    if (imageFile) {
      formData.append("image", imageFile);
    }
    handleAdd(formData);
  };

  useEffect(() => {
    if (resetForm) {
      setName("");
      setCode("");
      setDescription("");
      setSelectedCategory("");
      setSelectedShop(hasSingleShop ? String(shops[0].id) : "");
      setImageFile(null);
      setBrandName("");
      onClose();
    }
  }, [resetForm, onClose, hasSingleShop, shops]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" dir={fieldDirection}>
      <DialogTitle>{t("product.addProduct")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1, direction: fieldDirection }}>
          <TextField
            autoFocus
            margin="dense"
            label={t("product.productName")}
            type="text"
            fullWidth
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            {...getRtlTextFieldProps(formDirection)}
          />
          <TextField
            margin="dense"
            label={t("product.productCode")}
            type="text"
            fullWidth
            value={code}
            onChange={(e) => setCode(e.target.value)}
            {...getRtlTextFieldProps(formDirection)}
          />
          <TextField
            margin="dense"
            label={t("product.description")}
            type="text"
            fullWidth
            multiline
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            {...getRtlTextFieldProps(formDirection)}
          />
          <TextField
            select
            margin="dense"
            label={t("product.selectCategory")}
            fullWidth
            required
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            SelectProps={selectProps}
            {...getRtlTextFieldProps(formDirection)}
          >
            {catLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.name}
                </MenuItem>
              ))
            )}
          </TextField>
          <TextField
            select
            margin="dense"
            label={t("customer.selectShop")}
            fullWidth
            required
            value={selectedShop}
            onChange={(e) => setSelectedShop(e.target.value)}
            disabled={shopLoading}
            SelectProps={selectProps}
            {...getRtlTextFieldProps(formDirection)}
          >
            {shopLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              (shops || []).map((shop) => (
                <MenuItem key={shop.id} value={String(shop.id)}>
                  {shop.name}
                </MenuItem>
              ))
            )}
          </TextField>
          <Box sx={{ mt: 2, textAlign: fieldTextAlign }}>
            <Button variant="contained" component="label">
              {t("product.uploadImage")}
              <input type="file" hidden onChange={handleFileChange} />
            </Button>
            {imageFile && (
              <Typography variant="body2" sx={{ mt: 1, textAlign: fieldTextAlign }}>
                {imageFile.name}
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("product.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading || !name.trim() || !selectedCategory}
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("product.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductAdd;
